<script setup lang="ts">
import type { Result } from '~/types/games'

const props = withDefaults(defineProps<{
  item: Result
  variant?: 'ghost' | 'subtle' | 'solid' | 'outline' | 'soft' | 'naked'
  maxGenres?: number
  maxPlatforms?: number
  maxStores?: number
}>(), {
  variant: 'ghost',
  maxGenres: 2,
  maxPlatforms: 2,
  maxStores: 2
})

function formatBRDate(dateStr?: string | null) {
  if (!dateStr) return null
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return dateStr
  return d.toLocaleDateString('pt-BR')
}

const releasedLabel = computed(() => {
  if (props.item.tba) return 'Em breve (TBA)'
  const f = formatBRDate(`${props.item.released}`)
  return f ? `Lançamento: ${f}` : null
})

const ratingLabel = computed(() => {
  if (props.item.rating == null) return null
  const top = props.item.rating_top ?? 5
  return `⭐ ${Number(props.item.rating).toFixed(1)}/${top}`
})

const metaRightLabel = computed(() => {
  const parts: string[] = []

  if (props.item.metacritic != null) parts.push(`MC ${props.item.metacritic}`)
  if (props.item.playtime != null && props.item.playtime > 0) parts.push(`⏱ ${props.item.playtime}h`)

  return parts.length ? parts.join(' • ') : null
})

const genreBadges = computed(() =>
  (props.item.genres ?? []).slice(0, props.maxGenres)
)

const platformBadges = computed(() => {
  const parents = (props.item.parent_platforms ?? [])
    .map(p => p.platform)
    .filter(Boolean)
  if (parents.length) return parents.slice(0, props.maxPlatforms)

  const plats = (props.item.platforms ?? [])
    .map(p => p.platform)
    .filter(Boolean)
  return plats.slice(0, props.maxPlatforms)
})

const storeBadges = computed(() =>
  (props.item.stores ?? [])
    .map(s => s.store)
    .filter(Boolean)
    .slice(0, props.maxStores)
)

const countLabel = computed(() => {
  const a = props.item.ratings_count
  const b = props.item.reviews_text_count
  if (a != null && a > 0) return `(${a} avaliações)`
  if (b != null && b > 0) return `(${b} reviews)`
  return null
})
</script>

<template>
  <UPageCard
    :variant="variant"
    :title="item.name"
    :ui="{
      container: 'p-1 sm:p-4',
      title: 'text-base font-semibold line-clamp-1',
      description: 'sm:mt-1',
      footer: 'mt-0 pt-1 sm:mt-2 sm:pt-4'
    }"
  >
    <template #description>
      <div class="space-y-2">
        <div
          v-if="releasedLabel || metaRightLabel"
          class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-400"
        >
          <span v-if="releasedLabel">{{ releasedLabel }}</span>
          <span v-if="metaRightLabel">{{ metaRightLabel }}</span>
        </div>

        <div class="hidden sm:flex flex-wrap gap-1.5 ">
          <UBadge
            v-for="genre in genreBadges"
            :key="`g-${genre.id ?? genre.name}`"
            :label="translateCategory(genre.name)"
            color="primary"
            variant="subtle"
          />

          <UBadge
            v-for="p in platformBadges"
            :key="`p-${p.id}`"
            :label="p.name"
            color="primary"
            variant="subtle"
          />

          <UBadge
            v-for="s in storeBadges"
            :key="`s-${s.id}`"
            :label="s.name"
            color="primary"
            variant="subtle"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center justify-between gap-3 text-sm text-white/90">
        <span
          v-if="ratingLabel"
          class="whitespace-nowrap"
        >
          {{ ratingLabel }}
          <span
            v-if="countLabel"
            class="text-white/60"
          >{{ countLabel }}</span>
        </span>

        <span
          v-else
          class="text-white/60"
        >Sem nota</span>
      </div>
    </template>
  </UPageCard>
</template>
